import { Box, TextField, Typography } from "@mui/material";
import { extractIdFromIri } from "./extractFromIri";

export const toggleSection = (setStateFunction, selector) => {
  setStateFunction((prevState) => {
    const nextState = !prevState;
    setTimeout(() => {
      const element = document.querySelector(selector);
      if (nextState && element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
    return nextState;
  });
};
export const textFieldSx = {
  marginTop: 0,
  marginBottom: "2%",
  borderRadius: "6px",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#2E404D",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,91,176,0.75)",
      boxShadow: "1px 0px 3px 0px rgba(0,91,176,0.75)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1976d2",
      boxShadow: "none",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "9px 12px",
    fontFamily: "Noto Sans, sans-serif",
    "&::placeholder": {
      color: "#2E404D",
      fontFamily: "Noto Sans, sans-serif",
    },
  },
};

export const DateField = ({ label, name, value, onChange }) => (
  <Box display="flex" flexDirection="column" mr={2}>
    <Typography className="ptitleinput">{label}</Typography>
    <TextField
      fullWidth
      type="date"
      name={name}
      value={value}
      onChange={onChange}
      InputLabelProps={{ shrink: true }}
      variant="outlined"
      sx={textFieldSx}
    />
  </Box>
);

export function getSubdomainAndDomain() {
  const hostname = window.location.hostname;

  const parts = hostname.split(".");
  if (parts.length < 3) {
    return {
      subdomain: null,
      domain: hostname,
    };
  }

  const subdomain = parts[0] === "www" ? parts[1] : parts[0];
  const domain = parts.slice(-2).join(".");

  return {
    subdomain,
    domain,
  };
}

const i18nToBackendLangMap = {
  en: "eng",
  es: "spa",
  ca: "cat",
  fr: "fre",
  de: "ger",
};

export const getBackendLangCode = (i18nLang) => {
  if (!i18nLang) return "eng"; // fallback
  const base = String(i18nLang).split(/[-_]/)[0].toLowerCase();
  return i18nToBackendLangMap[base] || base;
};

export function getCodeFromLabel(vocabularyList, label, currentLang) {
  if (!Array.isArray(vocabularyList) || vocabularyList.length === 0)
    return null;
  if (label === undefined || label === null) return null;

  const normalize = (s) =>
    s === undefined || s === null ? "" : String(s).trim().toLowerCase();

  const labels = Array.isArray(label)
    ? label.map(normalize).filter(Boolean)
    : [normalize(label)].filter(Boolean);

  if (labels.length === 0) return null;

  const backendLang = getBackendLangCode(currentLang);

  const matches = labels
    .map((lbl) => {
      const byDirectLabel = vocabularyList.find(
        (item) => item?.label && normalize(item.label) === lbl,
      );
      if (byDirectLabel) {
        return byDirectLabel.concept_iri || byDirectLabel.code;
      }

      const byLang = vocabularyList.find(
        (item) =>
          item?.langName?.[backendLang] &&
          normalize(item.langName[backendLang]) === lbl,
      );
      if (byLang) return byLang.concept_iri || byLang.code;

      const byAny = vocabularyList.find((item) =>
        [item?.label, ...Object.values(item.langName || {})].some(
          (t) => typeof t === "string" && normalize(t) === lbl,
        ),
      );
      return byAny ? byAny.concept_iri || byAny.code : null;
    })
    .filter(Boolean);

  return matches.length > 1 ? matches : matches[0] || null;
}

const stripIriValue = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) return value.map(stripIriValue).filter(Boolean);
  if (typeof value !== "string") return "";
  return value.includes("/")
    ? value.substring(value.lastIndexOf("/") + 1)
    : value;
};

const getObjectControlledValue = (value, currentLang) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const backendLang = getBackendLangCode(currentLang);

  return (
    value.concept_iri ||
    value.code ||
    value.value ||
    value.label ||
    value.notation ||
    value.id ||
    value.langName?.[backendLang] ||
    value.langName?.eng ||
    value.langName?.spa ||
    Object.values(value.langName || {}).find(Boolean) ||
    null
  );
};

export function normalizeControlledValue({
  rawValue,
  vocabulary = [],
  currentLang,
  stripIri = false,
  multiple = false,
}) {
  const normalizedRawValue = Array.isArray(rawValue)
    ? rawValue
        .map((item) =>
          typeof item === "object"
            ? getObjectControlledValue(item, currentLang)
            : item,
        )
        .filter(Boolean)
    : typeof rawValue === "object"
      ? getObjectControlledValue(rawValue, currentLang)
      : rawValue;

  const fromLabel = getCodeFromLabel(
    vocabulary,
    normalizedRawValue,
    currentLang,
  );
  const resolved = fromLabel ?? normalizedRawValue;
  const normalized = stripIri ? stripIriValue(resolved) : resolved;

  if (multiple) {
    if (Array.isArray(normalized)) return normalized.filter(Boolean);
    return normalized ? [normalized] : [];
  }

  if (Array.isArray(normalized)) return normalized[0] || "";
  return normalized || "";
}

export const validateVersion = (value, t) => {
  const pattern = /^(\d+\.\d+)$|^(\d+\.\d+\.\d+)$/;

  if (!value.trim()) {
    return { valid: true, error: "" };
  }

  return {
    valid: pattern.test(value),
    error: pattern.test(value)
      ? ""
      : t("createDataModelDetail.detail.invalid_version"),
  };
};

export const validateURL = (value, t) => {
  if (Array.isArray(value)) value = value[0] || "";
  if (typeof value !== "string") value = "";

  if (!value.trim()) return { valid: true, error: "" };

  try {
    const url = new URL(value);
    const hasValidProtocol = ["http:", "https:"].includes(url.protocol);
    const domainPattern = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
    const isRealisticDomain = domainPattern.test(url.hostname);

    if (hasValidProtocol && isRealisticDomain) {
      return { valid: true, error: "" };
    }
  } catch (_) {}

  return {
    valid: false,
    error: t("createDataModelDetail.detail.invalid_url"),
  };
};

export const getControlledVocabulary = async (
  callApiWithWorker,
  vocabularyType,
  connector = "springConnector",
) => {
  try {
    const response = await callApiWithWorker(
      `api/graphql/vocabularies?vocabulary_type=${vocabularyType}`,
      { method: "GET", connector },
    );

    const items =
      response?.data?.getControlledVocabularies?.result?.[0]?.items ||
      response?.getControlledVocabularies?.result?.[0]?.items ||
      [];

    return items;
  } catch (error) {
    console.error("Error fetching controlled vocabulary:", error);
    return [];
  }
};

export const getControlledList = async (
  callApiWithWorker,
  vocabularyType,
  {
    lang,
    page = 1,
    size = 1000,
    connector = "springConnector",
    cacheMaxAgeMs = 12 * 60 * 60 * 1000,
  } = {},
) => {
  if (!getControlledList.memoryCache) {
    getControlledList.memoryCache = new Map();
  }

  if (!getControlledList.pendingRequests) {
    getControlledList.pendingRequests = new Map();
  }

  const cacheVersion = "controlled-list-v1";
  const cacheKey = [
    "controlled-list",
    cacheVersion,
    connector,
    vocabularyType,
    lang || "default",
    page,
    size,
  ].join("::");

  try {
    const memoryEntry = getControlledList.memoryCache.get(cacheKey);
    if (
      Array.isArray(memoryEntry?.items) &&
      Date.now() - Number(memoryEntry.updatedAt || 0) < cacheMaxAgeMs
    ) {
      return memoryEntry.items;
    }

    if (getControlledList.pendingRequests.has(cacheKey)) {
      return getControlledList.pendingRequests.get(cacheKey);
    }

    const requestPromise = callApiWithWorker(
      `/api/vocabularyControlled/get/${vocabularyType}?lang=${encodeURIComponent(
        lang,
      )}&page=${page}&size=${size}`,
      {
        method: "GET",
        connector,
        cacheMode: "cache-first",
        cacheMaxAgeMs,
      },
    ).then((response) => {
      const items =
        response?.items ||
        response?.body?.items ||
        (typeof response?.body === "string"
          ? JSON.parse(response.body)?.items
          : []) ||
        [];

      const cacheEntry = {
        cacheVersion,
        items,
        updatedAt: Date.now(),
      };

      getControlledList.memoryCache.set(cacheKey, cacheEntry);

      return items;
    });

    getControlledList.pendingRequests.set(cacheKey, requestPromise);

    const items = await requestPromise;
    getControlledList.pendingRequests.delete(cacheKey);

    return items;
  } catch (error) {
    getControlledList.pendingRequests?.delete(cacheKey);
    console.error("Error fetching controlled vocabulary:", error);
    return [];
  }
};

const getFirstReason = (error) => {
  if (!error?.reason || !Array.isArray(error.reason)) return "-";
  return error.reason[0] ?? "-";
};

const getNegotiationReasonShort = (error) => {
  const reason = getFirstReason(error);
  if (typeof reason !== "string") return "-";
  const parts = reason.split(":");
  return parts.length >= 2 ? parts[1].trim() : reason;
};

export const getDetailsFieldsByBlock = (t) => ({
  A: () => [
    { key: "resource_name", label: t("privateArea.actionLog.resource_name") },
    {
      key: "publisher",
      label: t("privateArea.actionLog.publisher"),
      render: (value) => extractIdFromIri(value) || value || "-",
    },
    {
      key: "created",
      label: t("privateArea.actionLog.created"),
      render: (value) => new Date(value).toLocaleString(),
    },
    {
      key: "modified",
      label: t("privateArea.actionLog.modified"),
      render: (value) => new Date(value).toLocaleString(),
    },
  ],
  B: () => [
    { key: "resource_name", label: t("privateArea.actionLog.resource_name") },
    {
      key: "publisher",
      label: t("privateArea.actionLog.publisher"),
      render: (value) => extractIdFromIri(value) || value || "-",
    },
    {
      key: "created",
      label: t("privateArea.actionLog.created"),
      render: (value) => new Date(value).toLocaleString(),
    },
    {
      key: "modified",
      label: t("privateArea.actionLog.modified"),
      render: (value) => new Date(value).toLocaleString(),
    },
  ],
  C: (row) => {
    const fields = [
      { key: "resource_title", label: t("privateArea.actionLog.offer") },
      { key: "username", label: t("privateArea.actionLog.user") },
      { key: "transfer_type", label: t("privateArea.actionLog.transfer") },
      { key: "status", label: t("privateArea.actionLog.status") },
      {
        key: "created",
        label: t("privateArea.actionLog.disp"),
        render: (value) => new Date(value).toLocaleString(),
      },
    ];

    if (row?.transfer_error) {
      fields.push({
        key: "transfer_error",
        label: t("privateArea.actionLog.errorReason"),
        render: (value) => getFirstReason(value),
      });
    }

    return fields;
  },
  D: (row) => {
    const fields = [
      { key: "resource_title", label: t("privateArea.actionLog.offer") },
      { key: "provider_name", label: t("privateArea.actionLog.publisher") },
      { key: "username", label: t("privateArea.actionLog.user") },
      { key: "status", label: t("privateArea.actionLog.status") },
      {
        key: "start_date",
        label: t("privateArea.actionLog.startDate"),
        render: (value) => new Date(value).toLocaleString(),
      },
    ];

    if (row?.negotiation_error) {
      fields.push({
        key: "negotiation_error",
        label: t("privateArea.actionLog.errorReason"),
        render: (value) => getNegotiationReasonShort(value),
      });
    }

    return fields;
  },
});

export const renderThemes = (themes) => {
  if (!themes) return <span className="themedata">N/A</span>;
  return Array.isArray(themes) ? (
    themes.map((item, i) => (
      <span key={i} className="themedata mb2">
        {item}
      </span>
    ))
  ) : (
    <span className="themedata">{themes}</span>
  );
};

export const getTranslationsQuality = (t) => ({
  scoreSuffix: t(
    "dataOfferingsDetail.detail.qualityScore.qualityView.summary.scoreSuffix",
  ),
  overallQuality: t(
    "dataOfferingsDetail.detail.qualityScore.qualityView.summary.overallQuality",
  ),
  qualityDescription:
    "dataOfferingsDetail.detail.qualityScore.qualityView.summary.description",
  qualityLink: t(
    "dataOfferingsDetail.detail.qualityScore.qualityView.summary.link",
  ),
  detailsTitle: t(
    "dataOfferingsDetail.detail.qualityScore.qualityView.details.title",
  ),

  scoreLabels: {
    labelExcellent: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.summary.labelExcellent",
    ),
    labelGood: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.summary.labelGood",
    ),
    labelFair: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.summary.labelFair",
    ),
    labelPoor: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.summary.labelPoor",
    ),
  },
  dimensions: {
    findability: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.dimensions.findability",
    ),
    accessibility: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.dimensions.accessibility",
    ),
    interoperability: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.dimensions.interoperability",
    ),
    reusability: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.dimensions.reusability",
    ),
    contextuality: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.dimensions.contextuality",
    ),
  },

  statuses: {
    pass: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.details.statusPass",
    ),
    fail: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.details.statusFail",
    ),
    warn: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.details.statusWarn",
    ),
  },

  metadataChecks: {
    dcatKeyword: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dcatKeyword",
    ),
    dcatTheme: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dcatTheme",
    ),
    dcatAccessURL: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dcatAccessURL",
    ),
    dcatDownloadURL: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dcatDownloadURL",
    ),
    dctFormat: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dctFormat",
    ),
    dcatMediaType: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dcatMediaType",
    ),
    dcatContactPoint: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dcatContactPoint",
    ),
    dctLicense: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dctLicense",
    ),
    dctIssued: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dctIssued",
    ),
    dctModified: t(
      "dataOfferingsDetail.detail.qualityScore.qualityView.metadataChecks.dctModified",
    ),
  },
});

export function cleanObject(obj) {
  if (Array.isArray(obj)) {
    const cleanedArray = obj
      .map((item) => cleanObject(item))
      .filter((item) => item !== undefined && item !== null);

    return cleanedArray.length > 0 ? cleanedArray : undefined;
  }

  if (typeof obj === "object" && obj !== null) {
    if (
      "language" in obj &&
      "value" in obj &&
      (obj.value === "" || obj.value === undefined || obj.value === null)
    ) {
      return undefined;
    }

    const cleanedObj = {};

    Object.keys(obj).forEach((key) => {
      const cleanedValue = cleanObject(obj[key]);

      if (
        cleanedValue !== undefined &&
        cleanedValue !== null &&
        cleanedValue !== "" &&
        !(
          typeof cleanedValue === "object" &&
          Object.keys(cleanedValue).length === 0
        )
      ) {
        cleanedObj[key] = cleanedValue;
      }
    });

    return Object.keys(cleanedObj).length > 0 ? cleanedObj : undefined;
  }

  if (obj === "" || obj === null || obj === undefined) {
    return undefined;
  }

  return obj;
}

export const extractText = (value) => {
  if (value == null) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(extractText).join(" ");
  }

  if (typeof value === "object") {
    return Object.values(value).map(extractText).join(" ");
  }

  return "";
};

export const fallbackDeep = (value, empty = "—") => {
  if (value === null || value === undefined || value === "") {
    return empty;
  }

  if (Array.isArray(value)) {
    return value.length === 0
      ? empty
      : value.map((item) => fallbackDeep(item, empty));
  }

  if (typeof value === "object") {
    return Object.keys(value).length === 0
      ? empty
      : Object.fromEntries(
          Object.entries(value).map(([key, val]) => [
            key,
            fallbackDeep(val, empty),
          ]),
        );
  }

  return value;
};

export function formatDate(dateInput, locale = "es-ES") {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
